'use client'

import { Fragment, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { IDynamicIslandItemProps } from '@/features/dynamic-island/dynamic-island-item'
import { useDynamicIslandContext } from '@/features/dynamic-island/dynamic-island-context'
import { useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import { ChevronDown, Download, Package, Tags, Trash } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/utils/styles/cn'
import { run } from 'shared'
import { defineNodeActions } from '@/features/drive/core/utils/use-define-node-actions'
import { NodeMoveActionSubMenu } from '@/features/drive/functionalities/actions/node/core/node-move-action/node-move-action-sub-menu'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'
import {
    DirectorySelectionBulkLabelsDialog,
    DirectorySelectionBulkProductsDialog,
} from '@/features/drive/functionalities/selection/directory-selection-bulk-connections-action'

import { getDirectorySelectionScope } from '@/features/drive/functionalities/selection/directory-selection-scope'

type ISelectionState = 'idle' | 'selected'

export const DirectorySelectionDynamicIsland = () => {
    const { nodePath, isPreview } = useDirectoryContext()

    const {
        state: { selectedCount, selectedNodes },
        download: { downloadSelected },
        deletion: { openDeleteSelectedDialog },
        methods: { clearSelection },
    } = useDirectorySelectionContext()

    const { setState } = useDynamicIslandContext()

    const state = !isPreview && selectedCount > 0 ? 'selected' : 'idle'

    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [isBulkLabelsOpen, setIsBulkLabelsOpen] = useState(false)
    const [isBulkProductsOpen, setIsBulkProductsOpen] = useState(false)
    const { canEditLabels, canEditProducts, ignoredLabelCount, ignoredProductCount, folderCount } =
        getDirectorySelectionScope(selectedNodes ?? [])

    const { menuContentClassName, menuItemClassName } = {
        menuItemClassName:
            'text-[#FFFFFFF2] data-highlighted:bg-[#313136] data-[state=open]:bg-[#313136] data-highlighted:bg-linear-to-r data-highlighted:from-[#313136] data-highlighted:to-[#35353A] data-highlighted:text-[#FFFFFFF2] text-sm whitespace-nowrap relative cursor-default',
        menuContentClassName:
            'min-w-[134px] rounded-[6px] border border-[#FFFFFF14] bg-[#27272A] shadow-[0px_1px_0px_0px_rgba(255,255,255,0.20)_inset,0px_1px_2px_0px_rgba(0,0,0,0.40),0px_0px_0px_1px_#18181B]',
    }

    const dynamicIslandState = useMemo<Omit<IDynamicIslandItemProps<ISelectionState>, 'id'>>(() => {
        return {
            state,
            stateMap: {
                idle: 'idle',
                selected: 'content',
            },
            openingTimeoutMs: 100,
            closingTimeoutMs: 100,
            icon: { node: null, id: 'null' },
            render: () => {
                const spaces = String(selectedCount)
                    .split('')
                    .reduce((acc, item, index, arr) => {
                        const isDecimalOne = item === '1' && arr.length === 2 && index !== arr.length - 1

                        return acc + (isDecimalOne ? 0.5 : 1)
                    }, 0)

                const width = `${spaces}ch`

                if (selectedCount === 0) {
                    return null
                }

                return (
                    <div className="flex items-center">
                        <div>
                            <motion.div
                                className="inline-block"
                                initial={{
                                    width: '1ch',
                                    minWidth: '1ch',
                                    maxWidth: '1ch',
                                }}
                                animate={{
                                    width,
                                    minWidth: width,
                                    maxWidth: width,
                                }}
                                transition={{
                                    width: { duration: 0.07, ease: 'linear' },
                                }}
                            >
                                <motion.div
                                    initial={{ opacity: 0.8 }}
                                    animate={{ opacity: 1 }}
                                    key={'directory-upload-progress-counter' + selectedCount}
                                >
                                    {selectedCount}
                                </motion.div>
                            </motion.div>{' '}
                            items selected
                        </div>

                        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                            <DropdownMenuTrigger asChild>
                                <div
                                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                                    className="ml-2 rounded-[4px] bg-white/20 flex items-center justify-center h-6 w-6 cursor-pointer"
                                >
                                    <motion.div
                                        initial={{ rotate: isDropdownOpen ? 180 : 0 }}
                                        animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                                    >
                                        <ChevronDown className="stroke-white" size={16} />
                                    </motion.div>
                                </div>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent className={cn('w-56 mt-4', menuContentClassName)}>
                                <DropdownMenuItem
                                    onSelect={
                                        canEditLabels
                                            ? () => {
                                                  setIsDropdownOpen(false)
                                                  setIsBulkLabelsOpen(true)
                                              }
                                            : undefined
                                    }
                                    disabled={!canEditLabels}
                                    className={menuItemClassName}
                                >
                                    <Tags className="text-[#FFFFFF99] h-3.5 w-3.5" />
                                    <div className="flex min-w-0 flex-col items-start gap-0.5 whitespace-normal">
                                        <span>Edit labels</span>
                                        {run(() => {
                                            if (!canEditLabels) {
                                                return (
                                                    <span className="text-[11px] text-[#FFFFFF80] leading-tight">
                                                        Only available for asset files
                                                    </span>
                                                )
                                            }

                                            if (ignoredLabelCount > 0) {
                                                return (
                                                    <span className="text-[11px] text-[#FFFFFF80] leading-tight">
                                                        {ignoredLabelCount === 1
                                                            ? '1 unsupported item will be ignored'
                                                            : `${ignoredLabelCount} unsupported items will be ignored`}
                                                    </span>
                                                )
                                            }

                                            return null
                                        })}
                                    </div>
                                </DropdownMenuItem>

                                <DropdownMenuItem
                                    onSelect={
                                        canEditProducts
                                            ? () => {
                                                  setIsDropdownOpen(false)
                                                  setIsBulkProductsOpen(true)
                                              }
                                            : undefined
                                    }
                                    disabled={!canEditProducts}
                                    className={menuItemClassName}
                                >
                                    <Package className="text-[#FFFFFF99] h-3.5 w-3.5" />
                                    <div className="flex min-w-0 flex-col items-start gap-0.5 whitespace-normal">
                                        <span>Edit products</span>
                                        {run(() => {
                                            if (!canEditProducts) {
                                                return (
                                                    <span className="text-[11px] text-[#FFFFFF80] leading-tight">
                                                        Only available for asset files and folders
                                                    </span>
                                                )
                                            }

                                            if (ignoredProductCount > 0) {
                                                return (
                                                    <span className="text-[11px] text-[#FFFFFF80] leading-tight">
                                                        {ignoredProductCount === 1
                                                            ? '1 unsupported item will be ignored'
                                                            : `${ignoredProductCount} unsupported items will be ignored`}
                                                    </span>
                                                )
                                            }

                                            if (folderCount > 0) {
                                                return (
                                                    <span className="text-[11px] text-[#FFFFFF80] leading-tight">
                                                        Images inside selected folders are included
                                                    </span>
                                                )
                                            }

                                            return null
                                        })}
                                    </div>
                                </DropdownMenuItem>

                                {run(() => {
                                    const nodeActions = defineNodeActions({
                                        'toggle-favorite': null,

                                        'file-analysis': null,

                                        rename: null,

                                        download: downloadSelected && (
                                            <DropdownMenuItem onClick={downloadSelected} className={menuItemClassName}>
                                                <Download className="text-[#FFFFFF99] h-3.5 w-3.5" />
                                                Download
                                            </DropdownMenuItem>
                                        ),

                                        move: (
                                            <NodeMoveActionSubMenu
                                                name=""
                                                path={nodePath || '/'}
                                                level={0}
                                                isEntrypoint
                                                onSuccess={clearSelection}
                                                mode={selectedCount > 0 ? 'selected' : 'node'}
                                                as="dropdown-menu"
                                                styleType="dark"
                                            />
                                        ),

                                        delete: openDeleteSelectedDialog && (
                                            <DropdownMenuItem
                                                onClick={openDeleteSelectedDialog}
                                                className={menuItemClassName}
                                            >
                                                <Trash className="text-[#FFFFFF99] h-3.5 w-3.5" />
                                                Delete
                                            </DropdownMenuItem>
                                        ),
                                    })

                                    return (
                                        <Fragment>
                                            {nodeActions.map(({ name, node }) => (
                                                <Fragment
                                                    key={'directory-selection-dynamic-island' + 'selection' + name}
                                                >
                                                    {node}
                                                </Fragment>
                                            ))}
                                        </Fragment>
                                    )
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <DirectorySelectionBulkLabelsDialog
                            isOpen={isBulkLabelsOpen}
                            onOpenChange={setIsBulkLabelsOpen}
                        />

                        <DirectorySelectionBulkProductsDialog
                            isOpen={isBulkProductsOpen}
                            onOpenChange={setIsBulkProductsOpen}
                        />
                    </div>
                )
            },
        }
    }, [
        state,
        selectedCount,
        isDropdownOpen,
        menuContentClassName,
        downloadSelected,
        menuItemClassName,
        nodePath,
        canEditLabels,
        canEditProducts,
        ignoredLabelCount,
        ignoredProductCount,
        folderCount,
        isBulkLabelsOpen,
        isBulkProductsOpen,
        clearSelection,
        openDeleteSelectedDialog,
    ])

    useEffect(() => {
        setState<ISelectionState>({ id: 'selected' }, () => ({
            ...dynamicIslandState,
            id: 'selected',
            priority: 1,
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicIslandState])

    return null
}
